import { Link } from "react-router-dom";
import { Reveal } from "@/components/motion";
import { useSEO } from "@/components/SEO";
import { SITE } from "@/lib/site";

function LegalLayout({ eyebrow, title, children, testid }) {
  return (
    <div data-testid={testid}>
      <section className="bg-[#121212] text-[#F6F3EB] pt-32 pb-14 md:pt-40 md:pb-16 texture-grain">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C7B5D]">{eyebrow}</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">{title}</h1>
          </Reveal>
        </div>
      </section>
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-[#D8D4CA] p-6 sm:p-10 space-y-8">{children}</div>
          <p className="mt-6 text-sm text-[#4A4D53]">
            Une question ? <Link to="/contact" data-testid={`${testid}-contact-link`} className="underline underline-offset-4 hover:text-[#8C7B5D]">Contactez-nous</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#121212]">{title}</h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-[#4A4D53]">{children}</div>
    </div>
  );
}

export function MentionsLegales() {
  useSEO(
    "Mentions légales | BM MECHANICS Prilly",
    "Mentions légales du site BM MECHANICS, atelier de mécanique indépendant à Prilly, Canton de Vaud, Suisse."
  );
  return (
    <LegalLayout eyebrow="Légal" title="Mentions légales" testid="mentions-legales-page">
      <Block title="Éditeur du site">
        <p>
          BM MECHANICS — Atelier de mécanique indépendant<br />
          Route de Cossonay 60, 1008 Prilly<br />
          Ouest lausannois, Canton de Vaud, Suisse
        </p>
        <p>
          Téléphone : <a href={SITE.phoneHref} className="underline underline-offset-4">{SITE.phoneDisplay}</a><br />
          Email : <a href={SITE.emailHref} className="underline underline-offset-4">{SITE.email}</a>
        </p>
        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#9A968C]">
          Numéro IDE : à compléter par l'entreprise
        </p>
      </Block>
      <Block title="Propriété intellectuelle">
        <p>
          L'ensemble des contenus de ce site (textes, logo, mise en page) est la propriété
          de BM MECHANICS. Toute reproduction sans autorisation écrite est interdite.
        </p>
      </Block>
      <Block title="Responsabilité">
        <p>
          Les informations publiées sur ce site le sont à titre indicatif. Les tarifs
          sont communiqués exclusivement sur devis. BM MECHANICS décline toute
          responsabilité quant à l'utilisation des informations du site.
        </p>
      </Block>
      <Block title="Droit applicable">
        <p>
          Le présent site est soumis au droit suisse. Tout litige sera soumis aux
          tribunaux compétents du Canton de Vaud.
        </p>
      </Block>
    </LegalLayout>
  );
}

export function Confidentialite() {
  useSEO(
    "Politique de confidentialité (LPD) | BM MECHANICS Prilly",
    "Politique de confidentialité de BM MECHANICS : traitement des données personnelles conformément à la loi suisse sur la protection des données (LPD)."
  );
  return (
    <LegalLayout eyebrow="Légal" title="Politique de confidentialité" testid="confidentialite-page">
      <Block title="Données collectées">
        <p>
          Lorsque vous utilisez nos formulaires de contact ou de demande de devis, nous
          collectons uniquement les données nécessaires au traitement de votre demande :
          nom, coordonnées (téléphone, email), informations relatives à votre véhicule
          et, le cas échéant, les photos que vous nous transmettez volontairement.
          Si vous utilisez l'assistant de discussion du site, le contenu de la conversation
          est conservé uniquement pour assurer le suivi de l'échange.
        </p>
      </Block>
      <Block title="Utilisation des données">
        <p>
          Vos données servent exclusivement à répondre à votre demande, établir un devis
          et organiser les interventions. Elles ne sont ni vendues, ni louées, ni
          transmises à des tiers à des fins commerciales.
        </p>
      </Block>
      <Block title="Conservation">
        <p>
          Les demandes sont conservées le temps nécessaire au traitement et au suivi de
          la relation client, conformément à la loi suisse sur la protection des
          données (LPD).
        </p>
      </Block>
      <Block title="Vos droits">
        <p>
          Conformément à la LPD, vous disposez d'un droit d'accès, de rectification et
          de suppression de vos données. Pour l'exercer, écrivez à{" "}
          <a href={SITE.emailHref} className="underline underline-offset-4">{SITE.email}</a>.
        </p>
      </Block>
    </LegalLayout>
  );
}

export function Cookies() {
  useSEO(
    "Politique des cookies | BM MECHANICS Prilly",
    "Politique d'utilisation des cookies du site BM MECHANICS, atelier mécanique à Prilly, Canton de Vaud."
  );
  return (
    <LegalLayout eyebrow="Légal" title="Cookies" testid="cookies-page">
      <Block title="Qu'est-ce qu'un cookie ?">
        <p>
          Un cookie est un petit fichier stocké sur votre appareil lors de la visite
          d'un site web. Il permet au site de fonctionner correctement et, le cas
          échéant, de mesurer son audience.
        </p>
      </Block>
      <Block title="Cookies utilisés sur ce site">
        <p>
          Ce site utilise uniquement des cookies et technologies strictement
          nécessaires à son fonctionnement ainsi qu'un outil de mesure d'audience
          anonyme destiné à améliorer le contenu proposé. Aucun cookie publicitaire
          n'est déposé.
        </p>
      </Block>
      <Block title="Gestion des cookies">
        <p>
          Vous pouvez à tout moment supprimer ou bloquer les cookies via les réglages
          de votre navigateur. La navigation sur le site reste possible sans cookies.
        </p>
      </Block>
    </LegalLayout>
  );
}
